import type { IConnectedAppResponse } from '@/openapi-clients-generated/suite';

export const CONNECTED_APPS_PATH = '/admin/integrations/connected-apps';
export const SLACK_CALLBACK_PATH = `${CONNECTED_APPS_PATH}/slack/callback`;

/** The API expires an authorization attempt after ten minutes; an older saved attempt can only fail. */
export const ATTEMPT_MAX_AGE_MS = 10 * 60 * 1000;

const ATTEMPT_KEY_PREFIX = 'zs:connectedApps:attempt:';

/**
 * What the browser remembers about one connect attempt while the user is away at the provider. Keyed by the
 * API's `state`, so concurrent attempts don't collide and an unsolicited callback finds nothing. Holds no
 * provider tokens or credentials — only who started it and where to go back to.
 */
export interface ConnectAttempt {
    provider: 'slack';
    tenantId: string;
    /** The MSP tenant impersonating `tenantId` when the attempt started, or null. */
    impersonatorTenantId: string | null;
    createdAt: number;
}

export function saveAttempt(storage: Storage, state: string, attempt: ConnectAttempt) {
    storage.setItem(ATTEMPT_KEY_PREFIX + state, JSON.stringify(attempt));
}

/** Reads and removes the attempt for `state`. One read only — a reloaded callback can't complete twice. */
export function takeAttempt(storage: Storage, state: string, now = Date.now()): ConnectAttempt | null {
    const key = ATTEMPT_KEY_PREFIX + state;
    const raw = storage.getItem(key);
    storage.removeItem(key);
    if (!raw) return null;
    try {
        const attempt = JSON.parse(raw) as ConnectAttempt;
        if (typeof attempt.tenantId !== 'string' || typeof attempt.createdAt !== 'number') return null;
        if (now - attempt.createdAt > ATTEMPT_MAX_AGE_MS) return null;
        return attempt;
    } catch {
        return null;
    }
}

/** Drops attempts the user abandoned at the provider, so they don't pile up in session storage. */
export function pruneAttempts(storage: Storage, now = Date.now()) {
    for (let i = storage.length - 1; i >= 0; i--) {
        const key = storage.key(i);
        if (!key?.startsWith(ATTEMPT_KEY_PREFIX)) continue;
        try {
            const attempt = JSON.parse(storage.getItem(key) ?? '') as ConnectAttempt;
            if (now - attempt.createdAt <= ATTEMPT_MAX_AGE_MS) continue;
        } catch {
            // Unreadable: drop it.
        }
        storage.removeItem(key);
    }
}

export type CallbackParams = { state: string; code: string; error?: undefined } | { state: string; error: string; code?: undefined };

/**
 * Reads the provider's redirect query. Exactly one of `code` / `error` must be present — the API rejects
 * both or neither — so anything else is treated as not a callback.
 */
export function parseCallbackQuery(query: Record<string, unknown>): CallbackParams | null {
    const pick = (value: unknown) => (Array.isArray(value) ? value[0] : value);
    const state = pick(query.state);
    const code = pick(query.code);
    const error = pick(query.error);
    if (typeof state !== 'string' || !state) return null;
    if (typeof code === 'string' && code && error === undefined) return { state, code };
    if (typeof error === 'string' && error && code === undefined) return { state, error };
    return null;
}

/**
 * Turns the API's absolute `returnUrl` into a router path. The API only issues same-origin URLs, but anything
 * else (or a URL outside the SPA's base path) falls back to the Connected Apps screen rather than leaving the app.
 */
export function returnUrlToRoute(returnUrl: string, origin: string, base: string): string {
    try {
        const url = new URL(returnUrl);
        if (url.origin !== origin) return CONNECTED_APPS_PATH;
        const prefix = base.replace(/\/+$/, '');
        if (prefix && url.pathname !== prefix && !url.pathname.startsWith(`${prefix}/`)) return CONNECTED_APPS_PATH;
        const path = url.pathname.slice(prefix.length) || '/';
        if (path.startsWith(SLACK_CALLBACK_PATH)) return CONNECTED_APPS_PATH;
        return path + url.search + url.hash;
    } catch {
        return CONNECTED_APPS_PATH;
    }
}

export const PROVIDER_ICONS: Record<string, string> = {
    slack: 'fa-brands fa-slack'
};

export function providerIcon(provider: string) {
    return PROVIDER_ICONS[provider] ?? 'fa-regular fa-plug';
}

/** Sorts by name, then workspace, so a tenant with several workspaces gets a stable list. */
export function sortConnections(connections: IConnectedAppResponse[]) {
    return [...connections].sort((a, b) => a.name.localeCompare(b.name) || a.externalName.localeCompare(b.externalName));
}
